import * as React from 'react';
import { DialerPhoneCallStatus, useTelephony } from '../../../contexts/telephony';
import { useQueue } from '../../../hooks/queue';
import { useTysonCoaching } from '../../contexts/TysonCoachingContext';
import { useRealCallCoaching } from '../../hooks/useRealCallCoaching';
import { useRealCallCoachingSession } from '../../hooks/useRealCallCoachingSession';
import { TranscriptEntry } from '../../types';

/**
 * Bridge component that connects real Twilio calls to the coaching system.
 * Renders nothing - purely handles side effects.
 */
export const RealCallCoachingBridge: React.FC = () => {
	const { phoneCall, phoneCallStatus } = useTelephony();
	const queue = useQueue();
	const coaching = useTysonCoaching();

	// Extract stable references to avoid dependency issues
	const registerFlushCallback = coaching.registerFlushCallback;
	const setLiveUtterances = coaching.setLiveUtterances;
	const sendTranscriptBatchImmediately = coaching.sendTranscriptBatchImmediately;
	const setCallEndedPendingSummary = coaching.setCallEndedPendingSummary;
	const setIsCallConnected = coaching.setIsCallConnected;
	const clearPendingTranscriptData = coaching.clearPendingTranscriptData;
	const sendNewCallStartedMessage = coaching.sendNewCallStartedMessage;

	const prevStatusRef = React.useRef<DialerPhoneCallStatus>(phoneCallStatus);
	// Store flush callback in ref so we can call it at end of call without effect dependencies
	const flushCallbackRef = React.useRef<(() => TranscriptEntry[]) | null>(null);
	// Refs to avoid re-firing status-transition effects when function references change
	const sendNewCallStartedMessageRef = React.useRef(sendNewCallStartedMessage);
	sendNewCallStartedMessageRef.current = sendNewCallStartedMessage;
	const clearPendingTranscriptDataRef = React.useRef(clearPendingTranscriptData);
	clearPendingTranscriptDataRef.current = clearPendingTranscriptData;

	const { isReady } = useRealCallCoachingSession({
		companyId: queue?.companyId || phoneCall?.companyId || null,
		contactId: phoneCall?.contactIds?.[0] || null,
		leadServedId: queue?.leadServedId,
		phoneCallId: phoneCall?.id,
	});

	// Bridge transcription events to coaching when call is connected
	const isCallActive = phoneCallStatus === DialerPhoneCallStatus.Connected;

	const { liveUtterances, flushAndGetEntries } = useRealCallCoaching({
		phoneCallId: phoneCall?.id || null,
		enabled: isCallActive && isReady,
	});

	// Keep flush callback ref updated while call is active
	React.useEffect(() => {
		flushCallbackRef.current = isCallActive && isReady ? flushAndGetEntries : null;
	}, [isCallActive, isReady, flushAndGetEntries]);

	// Register flush callback with context (for "Get Suggestion" button)
	React.useEffect(() => {
		if (isCallActive && isReady) {
			registerFlushCallback(flushAndGetEntries);
		} else {
			registerFlushCallback(null);
		}

		return () => {
			registerFlushCallback(null);
		};
	}, [isCallActive, isReady, flushAndGetEntries, registerFlushCallback]);

	// Sync live utterances to context
	React.useEffect(() => {
		setLiveUtterances(liveUtterances);
	}, [liveUtterances, setLiveUtterances]);

	// Flush remaining transcript and flag summary available when call ends
	React.useEffect(() => {
		const wasConnected = prevStatusRef.current === DialerPhoneCallStatus.Connected;
		const isNowEnded = phoneCallStatus === DialerPhoneCallStatus.NoCall;

		if (wasConnected && isNowEnded && isReady) {
			// Flush any remaining transcript entries
			if (flushCallbackRef.current) {
				const remainingEntries = flushCallbackRef.current();
				if (remainingEntries && remainingEntries.length > 0) {
					sendTranscriptBatchImmediately(remainingEntries);
				}
				flushCallbackRef.current = null;
			}

			// Show "Get Summary" button instead of auto-requesting
			setCallEndedPendingSummary(true);
		}

		prevStatusRef.current = phoneCallStatus;
	}, [phoneCallStatus, isReady, setCallEndedPendingSummary, sendTranscriptBatchImmediately]);

	// Reset state when new call starts — use refs for callbacks to prevent re-firing
	React.useEffect(() => {
		if (phoneCallStatus === DialerPhoneCallStatus.Dialing) {
			const contact = queue?.lead?.contacts?.[0];
			const contactName = contact
				? `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || 'Unknown Contact'
				: 'Unknown Contact';
			setCallEndedPendingSummary(false);
			clearPendingTranscriptDataRef.current();
			sendNewCallStartedMessageRef.current(contactName);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCallStatus, setCallEndedPendingSummary]);

	// Sync call connected state to context
	React.useEffect(() => {
		setIsCallConnected(phoneCallStatus === DialerPhoneCallStatus.Connected);
	}, [phoneCallStatus, setIsCallConnected]);

	return null;
};
